'use client';

import type { ReactNode } from 'react';
import { ChevronRight, Info } from 'lucide-react';
import { ResponsiveSheet, SheetPage } from '../components/ResponsiveSheet';
import { SEE_JESUS, SEE_JESUS_COPY } from './seeJesusBrand';

/** The See Jesus sun logo tucked into a top-right corner (mirrored from the pledge widget's top-left). */
export function SeeJesusCorner({ className = 'w-20 sm:w-24' }: { className?: string }) {
  return (
    <img
      src={SEE_JESUS.logoUrl}
      alt="See Jesus"
      className={`pointer-events-none h-auto select-none ${className}`}
      style={{ transform: 'scaleX(-1)' }}
    />
  );
}

/** The branded tag in a group's tag row; opens the explainer. */
export function SeeJesusTag({ onClick }: { onClick?: () => void }) {
  // The same quiet chip as the other tags, with the wordmark in gold so it
  // reads as a tag, not a second button.
  const cls =
    'sj-display inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-0.5 text-[15px] leading-6 text-[#e8c998] transition-all';
  if (!onClick) return <span className={cls}>See Jesus</span>;
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      title="What is a See Jesus Group?"
      className={`${cls} cursor-pointer hover:scale-105 hover:bg-white/15 active:scale-95`}
    >
      See Jesus
      <Info className="h-3.5 w-3.5 opacity-70" strokeWidth={2.5} />
    </button>
  );
}

/**
 * The double gold border from the pledge widget, scaled down: a foil ring,
 * a black gap, a second foil ring, then the black pane. Square corners on
 * rounded to sit with the rest of the finder's cards.
 */
export function SeeJesusFrame({
  children,
  className = '',
  paneClassName = '',
  corner,
}: {
  children: ReactNode;
  className?: string;
  paneClassName?: string;
  /** Sits over the top-right corner of the rings (the logo) */
  corner?: ReactNode;
}) {
  // `isolate` keeps the corner logo's z-index inside the card, so it cannot
  // rise above a page-level element such as the finder's floating help bar.
  return (
    <div
      className={`sj-foil sj-reveal relative isolate overflow-hidden rounded-2xl p-1 ${className}`}
    >
      {corner && <div className="absolute top-0 right-0 z-20">{corner}</div>}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-px rounded-[15px] border-2 border-black"
      />
      <div
        className={`sj-pane sj-type relative z-10 overflow-hidden rounded-[13px] ${paneClassName}`}
      >
        {children}
      </div>
    </div>
  );
}

/** Foil-filled button with blackletter text. */
export function SeeJesusButton({
  children,
  className = '',
  as = 'a',
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children: ReactNode;
  /** `span` when the button sits inside a card that is itself a link */
  as?: 'a' | 'span';
}) {
  const cls = `sj-foil sj-foil-btn inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-lg leading-none no-underline transition-all active:scale-[0.99] ${className}`;
  if (as === 'span') {
    return <span className={cls}>{children}</span>;
  }
  return (
    <a {...rest} className={cls}>
      {children}
    </a>
  );
}

/** The explainer, opened from the See Jesus tag. One dark glass panel. */
export function SeeJesusSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-3xl"
      noPanelPadding
      panelClassName="sj-glass overflow-hidden rounded-3xl text-[#f6efe9] shadow-[0_24px_80px_rgba(0,0,0,0.6)] ring-1 ring-[#c79854]/50"
    >
      <SheetPage name="main">
        <div className="sj-type relative flex flex-col">
          <div className="absolute top-0 right-0">
            <SeeJesusCorner className="w-28 sm:w-36" />
          </div>
          <div className="px-7 pt-10 pb-6 sm:px-10 sm:pt-12">
            <h2 className="sj-foil-text sj-display pr-28 text-4xl leading-none sm:pr-36 sm:text-5xl">
              {SEE_JESUS_COPY.title}
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#f6efe9]/85">
              {SEE_JESUS_COPY.lead}
            </p>
          </div>
          <div className="sj-foil h-px opacity-70" />
          <div className="flex flex-col gap-4 px-7 py-7 text-[15px] leading-relaxed text-[#f6efe9]/80 sm:px-10">
            {SEE_JESUS_COPY.body.map((p) => (
              <p key={p}>{p}</p>
            ))}
            <SeeJesusButton
              href={SEE_JESUS.campaignUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-fit"
            >
              {SEE_JESUS_COPY.campaignLink}
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </SeeJesusButton>
          </div>
        </div>
      </SheetPage>
    </ResponsiveSheet>
  );
}
